import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CalendarDays, Clock, MessageCircle, PenLine, ListTree } from "lucide-react";
import { useLanguage, waLink } from "@/i18n";
import { articles, getArticle, localizeArticle } from "@/data/articles";
import Seo, { SITE_URL } from "@/components/Seo";

export default function ArticlePage() {
  const { slug } = useParams();
  const { lang, t } = useLanguage();
  const raw = getArticle(slug);
  if (!raw) return <Navigate to="/artikel" replace />;
  const article = localizeArticle(raw, lang);

  const related = articles.filter((a) => a.slug !== slug).slice(0, 3);
  const author = article.author || "CV. Sinar Al Mubarokah";
  const toc = article.blocks
    .map((b, i) => (b.t === "h2" ? { id: `sec-${i}`, text: b.x } : null))
    .filter(Boolean);

  return (
    <main className="pt-24 sm:pt-28 pb-24 bg-[#0B1220] min-h-screen" data-testid="article-page">
      <Seo
        title={`${article.title} — CV. Sinar Al Mubarokah`}
        description={article.metaDesc || article.excerpt}
        path={`/artikel/${article.slug}`}
        type="article"
        image={article.img}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.metaDesc || article.excerpt,
          datePublished: article.date,
          image: article.img.startsWith("http") ? article.img : SITE_URL + article.img,
          author: { "@type": "Organization", name: author },
          publisher: { "@type": "Organization", name: "CV. Sinar Al Mubarokah" },
        }}
      />
      <article className="max-w-3xl mx-auto px-5 sm:px-8">
        <Link to="/artikel" data-testid="back-to-blog"
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-400 hover:text-sky-400 transition-colors">
          <ArrowLeft size={16} /> {t.blog.back}
        </Link>

        <motion.header initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="mt-8">
          <span className="inline-block bg-sky-500/10 border border-sky-500/30 text-sky-300 font-mono text-[10px] tracking-wider px-3 py-1 rounded-full" data-testid="article-keyword">
            {article.keyword}
          </span>
          <h1 className="mt-4 font-display font-extrabold tracking-tight text-white text-2xl sm:text-3xl lg:text-4xl leading-tight" data-testid="article-title">
            {article.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-500">
            <span className="flex items-center gap-1.5" data-testid="article-author">
              <PenLine size={13} className="text-sky-400" /> {t.blog.byAuthor} <span className="font-semibold text-slate-300">{author}</span>
            </span>
            <span className="flex items-center gap-1.5"><CalendarDays size={13} /> {article.date}</span>
            <span className="flex items-center gap-1.5"><Clock size={13} /> {article.minutes} {t.blog.min}</span>
          </div>
        </motion.header>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.15 }}
          className="mt-8 clip-industrial overflow-hidden border-4 border-slate-700/60 shadow-[0_30px_60px_-24px_rgba(0,0,0,0.6)]">
          <img src={article.img} alt={article.title} className="w-full aspect-[16/9] object-cover" data-testid="article-image" />
        </motion.div>

        {toc.length > 2 && (
          <motion.nav
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-8 bg-[#101B2E] border border-slate-800 rounded-2xl p-6 sm:p-7"
            data-testid="article-toc"
          >
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-sky-400 font-semibold flex items-center gap-2">
              <ListTree size={14} /> {t.blog.toc}
            </p>
            <ol className="mt-4 space-y-2">
              {toc.map((item, i) => (
                <li key={item.id}>
                  <a href={`#${item.id}`} data-testid={`toc-link-${i}`}
                    className="group flex items-start gap-3 text-sm sm:text-base text-slate-300 hover:text-sky-400 transition-colors">
                    <span className="font-mono text-sky-500/70 text-xs mt-0.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <span className="group-hover:underline underline-offset-4">{item.text}</span>
                  </a>
                </li>
              ))}
            </ol>
          </motion.nav>
        )}

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }}
          className="mt-10 space-y-6" data-testid="article-body">
          {article.blocks.map((b, i) => {
            if (b.t === "h2")
              return (
                <h2 key={i} id={`sec-${i}`} className="font-display font-extrabold tracking-tight text-slate-100 text-xl sm:text-2xl pt-4 scroll-mt-24">
                  {b.x}
                </h2>
              );
            if (b.t === "list")
              return (
                <ul key={i} className="space-y-2.5 bg-[#101B2E] border border-slate-800 rounded-2xl p-6">
                  {b.items.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-slate-300 text-base leading-relaxed">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-sky-400 shrink-0" /> {item}
                    </li>
                  ))}
                </ul>
              );
            return <p key={i} className="text-slate-400 text-base sm:text-lg leading-relaxed">{b.x}</p>;
          })}
        </motion.div>

        <div className="mt-12 bg-[#101B2E] border border-slate-800 rounded-3xl p-8 sm:p-10 text-center blueprint-grid-dark" data-testid="article-cta">
          <h2 className="font-display font-extrabold text-white text-xl sm:text-2xl tracking-tight">{t.blog.ctaTitle}</h2>
          <p className="mt-3 text-slate-400 text-sm sm:text-base max-w-md mx-auto">{t.blog.ctaSub}</p>
          <a href={waLink(t.keywords.askMsg(article.keyword))} target="_blank" rel="noopener noreferrer"
            data-testid="article-wa-btn"
            className="mt-6 inline-flex items-center gap-2.5 bg-sky-500 hover:bg-sky-400 text-white font-semibold px-8 py-4 rounded-full transition-colors">
            <MessageCircle size={19} /> {t.blog.ctaBtn}
          </a>
        </div>

        <div className="mt-14">
          <h2 className="font-display font-extrabold tracking-tight text-white text-xl sm:text-2xl">{t.blog.related}</h2>
          <div className="mt-6 space-y-3">
            {related.map((r) => {
              const a = localizeArticle(r, lang);
              return (
              <Link key={a.slug} to={`/artikel/${a.slug}`} data-testid={`related-article-${a.slug}`}
                className="group flex items-center justify-between gap-4 bg-[#101B2E] border border-slate-800 rounded-2xl px-6 py-4 hover:border-sky-500/60 transition-colors">
                <div>
                  <p className="font-display font-bold text-slate-100 text-sm sm:text-base group-hover:text-sky-400 transition-colors">{a.title}</p>
                  <p className="mt-1 font-mono text-[10px] tracking-wider text-sky-400">{a.keyword}</p>
                </div>
                <ArrowRight size={18} className="text-slate-500 group-hover:text-sky-400 shrink-0 transition-colors" />
              </Link>
              );
            })}
          </div>
        </div>
      </article>
    </main>
  );
}
